import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import StatsCard from "src/components/common/StatsCard";
import defaultStyles from "../styles";
import { getStyles } from "src/styles/theme";
import {
  getDashboardDetails,
  getTableDashboardDetails,
} from "src/utils/api/dashboard";
import { DashBoardTable } from "src/components/common/DashBoardTable";
import {
  downloadSheet,
  exportEcoRestorationTableToXlsx,
  exportTableToXlsx,
  formatData,
  formatDataForBarChart,
} from "src/utils/helper";
import DashboardChartSkeleton from "src/components/common/DashboardChartSkeleton";
import { useEffect, useMemo, useState } from "react";
import MultiSelect from "src/components/common/MultiSelect";
import { getStates } from "src/utils/api/roles";

import StatusBarChart from "src/components/common/StatusBarChart";
import RenderTable from "../../Home/RenderTable";
import { ecoRestorationDashboardtableColumns } from "src/constants/tableColumns";
import { AxiosResponse } from "axios";
import {
  EcoRestoartionDashBoardTable,
  ecoRestorationDashBoardTable,
} from "src/components/common/DashBoardTable/ecoRestoration";
import { usePersistedFilters } from "src/hooks/usePersistedFilters";

const EcoRestorationDashboard = () => {
  const [isTabLoading, setIsTabLoading] = useState(true);

  const [states, setStates] = useState<any>([]);

  const [tableRows, setTableRows] = useState([]);

  const [filtersState, setFiltersState] = useState([]);

  const { persisted, saveFilters } = usePersistedFilters("mw_ecoRestoration");

  const [queryParams, setQueryParams] = useState<{
    projectStateIds?: string[];
    type: string;
    fromDate?: string;
    toDate?: string;
  }>({
    projectStateIds: persisted?.queryParams?.projectStateIds ?? undefined,
    type: "landLeased",
    fromDate: persisted?.queryParams?.fromDate ?? undefined,
    toDate: persisted?.queryParams?.toDate ?? undefined,
  });

  const [filterKey, setFilterKey] = useState(
    persisted?.filterKey ?? {
      projectStateIds: null,
      type: "",
      fromDate: "",
      toDate: "",
    }
  );

  const { data: dashboard, isLoading: dashboardLoading } = useQuery(
    ["dashboard", queryParams],
    () => getDashboardDetails(queryParams),
    {
      onSuccess: () => {
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  const { data: tableDashboard, isLoading } = useQuery(
    ["table-landLeased", queryParams],
    () => getTableDashboardDetails(queryParams),
    {
      onSuccess: (res: AxiosResponse) => {
        setTableRows(
          res?.data?.stats.map((items, idx) => ({
            id: idx,
            sno: idx + 1,
            state: items.state,
            totalAreaNodalVerificationPending:
              items.totalAreaNodalVerificationPending,
            totalNumberNodalVerificationPending:
              items.totalNumberNodalVerificationPending,
            totalAreaVerificationPending: items.totalAreaVerificationPending,
            totalNumberVerificationPending:
              items.totalNumberVerificationPending,
            totalAreaUnderQuery: items.totalAreaUnderQuery,
            totalNumberUnderQuery: items.totalNumberUnderQuery,
            totalAreaNodalUnderQuery: items.totalAreaNodalUnderQuery,
            totalNumberNodalUnderQuery: items.totalNumberNodalUnderQuery,
            totalAreaApproved: items.totalAreaApproved,
            totalNumberApproved: items.totalNumberApproved,
            totalAreaOldApproved: items.totalAreaOldApproved,
            totalNumberOldApproved: items.totalNumberOldApproved,
            totalAreaRejected: items.totalAreaRejected,
            totalNumberRejected: items.totalNumberRejected,
            totalAreaWithdrawn: items.totalAreaWithdrawn,
            totalNumberWithdrawn: items.totalNumberWithdrawn,
            totalAreaRegistered: items.totalAreaRegistered,
            totalNumberRegistered: items.totalNumberRegistered,
          }))
        );
        setIsTabLoading(false);
      },
      onError: () => {},
      refetchOnMount: true,
    }
  );

  useQuery(["getStatesType"], () => getStates(), {
    onSuccess: (data) => {
      setStates(data.data);
    },
    refetchOnMount: true,
  });

  const dashboardTableData: ecoRestorationDashBoardTable = {
    topHeader: [
      "",
      "All Registered",
      "SNO Verification",
      "Admin Verification",
      "SNO Query",
      "Under Admin Query",
      "Approved under new OM",
      "Approved under old OM",
      "Rejected",
      "Withdrawn",
    ],
    header: [
      "S.No",
      "State",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
      "No",
      "Area (ha)",
    ],
    data: tableRows,
  };

  const formattedData = useMemo(
    () =>
      formatDataForBarChart(
        dashboard?.data?.stats?.filter((stat) => stat?.type !== "WITHDRAWN")
      ),
    [dashboard?.data?.stats]
  );

  const routesConfig = {
    REGISTERED: "/registered-lands",
    VERIFICATION_PENDING: "/registered-lands?currentTab=VERIFICATION_PENDING",
    UNDER_QUERY: "/registered-lands?currentTab=UNDER_QUERY",
    APPROVED: "/registered-lands?currentTab=APPROVED",
    OLD_APPROVED: "/registered-lands?currentTab=OLD_APPROVED",
    REJECTED: "/registered-lands?currentTab=REJECTED",
    NODAL_UNDER_QUERY: "/registered-lands?currentTab=NODAL_UNDER_QUERY",
    WITHDRAWN: "/registered-lands?currentTab=WITHDRAWN",
  };

  const styles = getStyles(defaultStyles);
  useEffect(() => {
    setIsTabLoading(true);
  }, []);

  useEffect(() => {
    setFiltersState(dashboard?.data?.state);
  }, [dashboard]);

  useEffect(() => {
    saveFilters({ queryParams, filterKey });
  }, [queryParams, filterKey]);

  return (
    <>
      <Box {...styles("topHeader")}>
        <Box
          sx={{
            marginBottom: "5px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {filtersState && (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                Selected States:-
                {filtersState?.map((filterState) => {
                  return (
                    <Box
                      sx={{
                        height: "46px",
                        padding: "11px",
                        width: "fit-content",
                        border: "1px solid grey",
                        borderRadius: "24px",
                        backgroundColor: "#eee",
                      }}
                    >
                      {filterState}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "0px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <FormControl sx={{ m: 1, width: 180 }}>
              <InputLabel
                shrink
                htmlFor="eco-from-date"
                sx={{ background: "#fff", px: 0.5 }}
              >
                From Date
              </InputLabel>
              <OutlinedInput
                id="eco-from-date"
                type="date"
                notched
                label="From Date"
                value={filterKey.fromDate ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterKey((prev) => ({ ...prev, fromDate: val }));
                  setQueryParams((prev) => ({
                    ...prev,
                    fromDate: val || undefined,
                  }));
                }}
                sx={{ height: 56 }}
              />
            </FormControl>
            <FormControl sx={{ m: 1, width: 180 }}>
              <InputLabel
                shrink
                htmlFor="eco-to-date"
                sx={{ background: "#fff", px: 0.5 }}
              >
                To Date
              </InputLabel>
              <OutlinedInput
                id="eco-to-date"
                type="date"
                notched
                label="To Date"
                value={filterKey.toDate ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilterKey((prev) => ({ ...prev, toDate: val }));
                  setQueryParams((prev) => ({
                    ...prev,
                    toDate: val || undefined,
                  }));
                }}
                sx={{ height: 56 }}
              />
            </FormControl>
            <MultiSelect
              options={states}
              value={filterKey.projectStateIds}
              onChange={(e: any) => {
                setFilterKey((prevFilter) => {
                  const newState = {
                    ...prevFilter,
                    projectStateIds: e.length ? e : null,
                  };

                  setQueryParams({
                    ...queryParams,
                    projectStateIds: newState?.projectStateIds,
                  });

                  return newState;
                });
              }}
            />
          </Box>
        </Box>

        {!dashboardLoading ? (
          <Box
            sx={{ width: "100%", marginBottom: "20px", alignSelf: "center" }}
          >
            <StatusBarChart
              data={formattedData}
              label={"Status OF Eco-Restoration Blocks"}
            />
          </Box>
        ) : (
          <></>
        )}
        <Box {...styles("statsCardWrapper", { marginTop: "20px" })}>
          {dashboard?.data?.stats?.map((stat, index) => (
            <StatsCard
              key={index}
              title={`${stat?.description?.toUpperCase()}`}
              value={`${stat?.number}`}
              area={stat?.totalArea}
              customStyles={{ valueCard: styles("statsCard").sx }}
              link={routesConfig[stat?.type]}
            />
          ))}
        </Box>
      </Box>

      {isLoading || isTabLoading ? (
        <DashboardChartSkeleton />
      ) : (
        <Box {...styles("dashboardTableWrapper")}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h5" {...styles("heading")}>
              Status of Eco-Restoration Blocks Under GCP
            </Typography>
            <Button
              variant="outlined"
              onClick={() =>
                exportEcoRestorationTableToXlsx(
                  dashboardTableData.data,
                  dashboardTableData.header,
                  dashboardTableData.topHeader
                )
              }
            >
              Export
            </Button>
          </Box>
          <EcoRestoartionDashBoardTable {...dashboardTableData} />
        </Box>
      )}
    </>
  );
};

export default EcoRestorationDashboard;
